import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ArchitecturePopupEditorProps } from "@/types/unified-model";
import StandardFieldsEditor from "./standard-fields-editor";
import DynamicFieldsEditor from "./dynamic-fields-editor";

type StandardData = {
  name: any;
  description: any;
  group: any;
} & Record<string, any>;

export default function ArchitecturePopupEditor({
  open,
  onOpenChange,
  component,
  onSave,
  // allArchitectureData = [],
}: ArchitecturePopupEditorProps) {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);

  // State for dynamic key-value pairs
  const [dynamicFields, setDynamicFields] = useState<
    Array<{ key: string; value: string }>
  >([{ key: "", value: "" }]);

  // State to track original data and show cancel alert
  const [originalFormData, setOriginalFormData] = useState<Record<string, any>>({});
  const [originalDynamicFields, setOriginalDynamicFields] = useState<Array<{ key: string; value: string }>>([]);
  const [showCancelAlert, setShowCancelAlert] = useState(false);

  // Constants
  const standardFields = ["name", "description", "group", "createdAt",
  "updatedAt",
  "projectUuid",
  "architecturalId"];
  const readOnlyFields = ["group",
  "createdAt",
  "updatedAt",
  "projectUuid",
  "architecturalId"];
  const [_, setOriginalDynamicKeys] = useState<string[]>([]);

  // Initialize component data
  useEffect(() => {
    if (component) {
      const componentData = component.metadata?.blueprint || component;
      const clonedData = JSON.parse(JSON.stringify(componentData));

      const standardData: Record<string, any> = {};
      const dynamicData: Array<{ key: string; value: string }> = [];
      const originalKeys: string[] = [];

      Object.entries(clonedData).forEach(([key, value]) => {
        if (standardFields.includes(key)) {
          standardData[key] = value;
        } else if (key !== "id" && key !== "metadata") {
          dynamicData.push({
            key,
            value: typeof value === "string" ? value : JSON.stringify(value),
          });
          originalKeys.push(key);
        }
      });

      if (dynamicData.length === 0) {
        dynamicData.push({ key: "", value: "" });
      } else {
        dynamicData.push({ key: "", value: "" });
      }

      setFormData(standardData);
      setDynamicFields(dynamicData);
      setOriginalDynamicKeys(originalKeys);

      // Store original data for change detection
      setOriginalFormData(JSON.parse(JSON.stringify(standardData)));
      setOriginalDynamicFields(JSON.parse(JSON.stringify(dynamicData)));
    }
  }, [component]);

  // Function to check if there are unsaved changes
  const hasUnsavedChanges = () => {
    const formDataChanged = JSON.stringify(formData) !== JSON.stringify(originalFormData);

    const currentNonEmptyFields = dynamicFields.filter(field => field.key.trim() !== "");
    const originalNonEmptyFields = originalDynamicFields.filter(field => field.key.trim() !== "");
    const dynamicFieldsChanged = JSON.stringify(currentNonEmptyFields) !== JSON.stringify(originalNonEmptyFields);

    return formDataChanged || dynamicFieldsChanged;
  };

  // Handle cancel button click
  const handleCancelClick = () => {
    if (hasUnsavedChanges()) {
      setShowCancelAlert(true);
    } else {
      onOpenChange(false);
    }
  };

  // Handle confirm discard changes
  const handleConfirmDiscard = () => {
    setShowCancelAlert(false);
    onOpenChange(false);
  };

  // Handle dialog close
  const handleDialogClose = (isOpen: boolean) => {
    if (!isSaving) {
      onOpenChange(isOpen);
    }
  };

  const handleSave = async () => {
    if (!component || isSaving) return;

    setIsSaving(true);
    try {
      const standardData: StandardData = {
        name: formData.name,
        description: formData.description,
        group: formData.group,
      };

      const currentDynamicKeys = dynamicFields
        .filter((field) => field.key.trim() !== "")
        .map((field) => field.key);

      dynamicFields.forEach(({ key, value }) => {
        if (key.trim()) {
          // Never send citationRefs in the update payload. It isn't included
          // here, so the existing value stays untouched in the backend.
          if (key === "citationRefs") return;
          try {
            standardData[key] = JSON.parse(value);
          } catch {
            standardData[key] = value;
          }
        }
      });

      // Create the updated component with the new data structure
      const updatedComponent = {
        ...component,
        id: component.id,
        group: standardData.group,
        metadata: {
          name: standardData.name,
          blueprint: {
            ...standardData,
          },
        },
      };

      // Call the parent's save function (which now handles Neo4j API calls)
      await onSave(updatedComponent);

      setOriginalDynamicKeys(currentDynamicKeys);

    } catch (error) {
      console.error("Error saving component:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-md bg-surface text-text-primary border-surface-border max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              Edit {formData.name || "Component"}
            </DialogTitle>
            <DialogDescription className="text-muted">
              Update the details for this architecture component
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <StandardFieldsEditor
              formData={formData}
              setFormData={setFormData}
              standardFields={standardFields}
              readOnlyFields={readOnlyFields}
            />

            <DynamicFieldsEditor
              dynamicFields={dynamicFields}
              setDynamicFields={setDynamicFields}
            />
          </div>

          <DialogFooter className="sm:justify-between">
            <Button
                variant="outline"
              onClick={handleCancelClick}
              className="bg-surface hover:bg-surface-hover text-text-primary border-surface-border"
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-primary hover:bg-primary-dark text-button-text"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Alert Dialog */}
      <AlertDialog open={showCancelAlert} onOpenChange={setShowCancelAlert}>
        <AlertDialogContent className="bg-surface-card border-surface-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white text-xl font-semibold">
              Discard Changes?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted text-sm">
              Are you sure you want to discard the changes?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowCancelAlert(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-primary text-white hover:bg-primary/90"
              onClick={handleConfirmDiscard}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}